import React, { FC } from "react";

import CheckCircle from "@uui/CheckCircle";

import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import { Button as MuiButton, ButtonProps } from "@mui/material";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import { Row, StackProps } from "@rui/Stack";

export const TextButton = styled<FC<ButtonProps>>((props) => (
  <MuiButton variant="text" {...props} />
))(({ theme }) => ({
  borderRadius: "8px",
  height: "46px",
  fontSize: "0.875rem",
  color: theme.palette.shades.neutral[500],
  minWidth: "106px",
}));

export const Button = styled<FC<LoadingButtonProps & { primary?: boolean }>>(
  ({ primary: _p, ...props }) => <LoadingButton variant="outlined" {...props} />
)(({ theme, primary }) => ({
  backgroundColor: primary ? theme.palette.shades.primary[500] : "white",
  border: `2px solid ${
    primary
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.neutral[100]
  }`,
  borderRadius: "8px",
  height: "46px",
  fontSize: "0.875rem",
  color: primary ? "white" : theme.palette.shades.primary[500],
  minWidth: "fit-content",
  padding: "8px 24px",
  "&:hover": {
    backgroundColor: primary
      ? theme.palette.shades.primary[600]
      : theme.palette.shades.primary[50],
    borderColor: theme.palette.shades.primary[500],
    borderWidth: "2px",
    color: primary ? "#FFF" : theme.palette.shades.primary[600],
  },
  "&.Mui-disabled": {
    backgroundColor: primary ? theme.palette.shades.primary[50] : undefined,
  },
  "&.MuiLoadingButton-loading": {
    borderWidth: "2px",
  },
}));

type IntegrationProviderRowProps = StackProps & { disabled: boolean };
export const IntegrationProviderRow = styled<
  React.FC<IntegrationProviderRowProps>
>(({ disabled: _d, ...props }) => <Row {...props} />)(
  ({ disabled, theme }) => ({
    padding: theme.spacing(1),
    borderRadius: theme.corners.base,
    transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    maxWidth: "100%",
    ...(!disabled && {
      "&:hover": {
        cursor: "pointer",
        backgroundColor: theme.palette.shades.ghost[300],
        "& .MuiButton-root": {
          backgroundColor: "#FFF",
        },
      },
    }),
  })
);

export const IntegrationConnectedChip = styled<React.FC<StackProps>>(
  (props) => (
    <Row {...props} variant="left" spacing={1}>
      <CheckCircle />
      <Typography variant="bodySecondaryMed" className="label">
        Connected
      </Typography>
    </Row>
  )
)(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
  borderRadius: theme.corners.small,
  backgroundColor: theme.palette.shades.meadow[50],
  padding: theme.spacing(1),
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
    color: theme.palette.shades.meadow[600],
  },
  [theme.breakpoints.down("sm")]: {
    "& .label": {
      display: "none",
    },
  },
}));

export const ConnectButton = styled<React.FC<LoadingButtonProps>>(
  ({ children, ...props }) => (
    <LoadingButton {...props} variant="outlined">
      {children ?? "Connect"}
    </LoadingButton>
  )
)(({ theme }) => ({
  padding: theme.spacing(1),
}));

export const RecommendedChip = styled<React.FC<TypographyProps>>((props) => (
  <Typography {...props}>Recommended</Typography>
))(({ theme }) => ({
  ...theme.typography.labelMed,
  color: theme.palette.shades.primary[500],
  padding: "2px 4px",
  backgroundColor: theme.palette.shades.ghost[500],
  borderRadius: theme.corners.small,
}));

export const WrapUpCard = styled(Row)(({ theme }) => ({
  width: "100%",
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: theme.corners.base,
  padding: theme.spacing(2),
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    "& .wrap-up-card__icon": {
      display: "none",
    },
  },
}));
